import { useEffect, useState } from 'react';
import { Download, Loader2, Receipt, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { contentEngineApi } from '../../services/contentEngineApi';

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; }
};

export function ReceiptHistory() {
  const [receipts, setReceipts] = useState(null);
  const [scheduled, setScheduled] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    contentEngineApi.listReceipts().then((d) => setReceipts(d.receipts || [])).catch(() => setReceipts([]));
    contentEngineApi.listPlans().then((d) => setScheduled(d?.subscription?.scheduledChange || null)).catch(() => {});
  }, []);

  const download = async (r) => {
    setBusy(r.razorpayOrderId);
    try {
      await contentEngineApi.downloadReceiptPdf(r.razorpayOrderId, `ActanonEdu-${r.receiptNo.replace(/\//g, '-')}.pdf`);
    } catch (err) {
      toast.error(err.message || 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="ae-surface p-5 sm:p-6" data-testid="receipt-history">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-9 w-9 rounded-xl bg-ae-navy text-ae-cyan flex items-center justify-center"><Receipt className="h-4 w-4" /></span>
        <div>
          <h2 className="text-base font-heading font-bold">Billing &amp; receipts</h2>
          <p className="text-xs text-muted-foreground">GST tax invoices for every plan purchase.</p>
        </div>
      </div>

      {scheduled && (
        <div className="mb-4 rounded-xl border border-ae-warning/30 bg-ae-warning/[0.07] px-4 py-3 text-xs flex items-start gap-2" data-testid="scheduled-change-banner">
          <CalendarClock className="h-4 w-4 text-amber-600 shrink-0" />
          <span>Your switch to <strong className="uppercase">{scheduled.tier}</strong> is scheduled for <strong>{fmtDate(scheduled.startsAt)}</strong>. You keep your current plan until then.</span>
        </div>
      )}

      {receipts === null ? (
        <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : receipts.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4" data-testid="receipt-history-empty">No payments yet — your receipts will appear here after your first upgrade.</p>
      ) : (
        <div className="divide-y divide-slate-200/80 dark:divide-slate-800">
          {receipts.map((r) => (
            <div key={r.razorpayOrderId} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`receipt-row-${r.razorpayOrderId}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold">{r.planName} plan · {inr(r.amount)}</div>
                <div className="text-[11px] text-muted-foreground">
                  {r.receiptNo} · Paid {fmtDate(r.paidOn)} · Renews {fmtDate(r.renewsOn)} · incl. GST {inr(r.gst?.gstAmount)}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => download(r)} disabled={busy === r.razorpayOrderId} data-testid={`receipt-download-${r.razorpayOrderId}`} className="gap-1.5">
                {busy === r.razorpayOrderId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
